/**
 * What appears in the sidebar.
 *
 * Grouped the way BEETS groups its sidebar, over BEST's much smaller surface.
 *
 * The important part is the last step: any entity kind in the registry that no
 * curated screen covers is appended automatically. So adding risks to
 * services/schema.py puts Risks in the nav with a working table and form,
 * without this file being edited - which is the same promise the back end
 * makes, kept on this side too.
 */

import type { ReactElement } from 'react'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'
import CategoryIcon from '@mui/icons-material/Category'
import ChecklistIcon from '@mui/icons-material/Checklist'
import DashboardIcon from '@mui/icons-material/Dashboard'
import FunctionsIcon from '@mui/icons-material/Functions'
import GavelIcon from '@mui/icons-material/Gavel'
import GridOnIcon from '@mui/icons-material/GridOn'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import MergeTypeIcon from '@mui/icons-material/MergeType'
import ScaleIcon from '@mui/icons-material/Scale'
import SchemaIcon from '@mui/icons-material/Schema'
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined'
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import WidgetsIcon from '@mui/icons-material/Widgets'
import type { Schema } from './schema'

export interface NavItem {
  label: string
  path: string
  icon: ReactElement
  tooltip: string
  /** The entity kind this screen shows, when it shows one. */
  kind?: string
}

export interface NavGroup {
  label: string
  items: NavItem[]
}

/** Screens that are more than a table of one kind, and the kinds they cover. */
const CURATED: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      {
        label: 'Dashboard',
        path: '/',
        icon: <DashboardIcon />,
        tooltip: 'Counts, coverage and what needs attention',
      },
      {
        label: 'Findings',
        path: '/findings',
        icon: <WarningAmberIcon />,
        tooltip: 'Consistency problems found in the model',
      },
      {
        label: 'Views',
        path: '/views',
        icon: <ViewQuiltIcon />,
        tooltip: 'Diagrams over the model, and the views waiting to be drawn',
        // Stated, or the registry's 'view' kind counts as uncovered and is
        // appended a second time under More, at this same path.
        kind: 'view',
      },
      {
        // Where Files used to be. The .sysml export moved here with it: a file
        // is a place text lands, and the tree is the thing people actually
        // want to look at.
        label: 'Model tree',
        path: '/model-tree',
        icon: <SchemaIcon />,
        tooltip: 'Every element, as the model declares it — and its .sysml',
      },
    ],
  },
  {
    label: 'Requirements',
    items: [
      {
        label: 'Requirements',
        path: '/requirements',
        icon: <AssignmentTurnedInIcon />,
        tooltip: 'Browse and edit requirements',
        kind: 'requirement',
      },
      {
        label: 'Flowdown',
        path: '/flowdown',
        icon: <AccountTreeIcon />,
        tooltip: 'The derivation tree, L0 down to L4',
      },
      {
        label: 'Traceability',
        path: '/rtm',
        icon: <ChecklistIcon />,
        tooltip: 'The requirements traceability matrix',
      },
    ],
  },
  {
    label: 'System design',
    items: [
      {
        // FIRST in the group, deliberately. It is the whole-system answer -
        // what the vehicle is, what it totals, what it is allowed - and
        // everything below it in this group is one part of that. Reading the
        // summary before the parts is the order somebody actually wants; it sat
        // last while it was called Budgets, which described a footnote rather
        // than the page.
        //
        // 'Budgets' until 2026-09-01, then 'System attributes' briefly. The
        // renames are not cosmetic: the page carries the system's own stated
        // properties - centre of gravity, inertia, delta-V - alongside the
        // rollups and their budgets, and several of those deliberately do not
        // aggregate at all.
        label: 'System Summary',
        path: '/system-summary',
        icon: <ScaleIcon />,
        tooltip: 'What the system is, what it totals, and what it is allowed',
      },
      {
        label: 'Parts',
        path: '/parts',
        icon: <WidgetsIcon />,
        tooltip: 'The system decomposition',
        kind: 'design_part',
      },
      {
        label: 'Ports',
        path: '/ports',
        icon: <SettingsInputComponentIcon />,
        tooltip: 'Where something crosses a boundary',
        kind: 'design_port',
      },
      {
        label: 'Interfaces',
        path: '/interfaces',
        icon: <SwapHorizIcon />,
        tooltip: 'Messages, ports and interfaces from the ICD',
        kind: 'interface',
      },
      {
        label: 'States',
        path: '/states',
        icon: <AccountTreeOutlinedIcon />,
        tooltip: 'The states a part can be in',
        kind: 'state',
      },
      {
        label: 'Functions',
        path: '/functions',
        icon: <FunctionsIcon />,
        tooltip: 'What the system does, and which part does it',
        kind: 'function',
      },
    ],
  },
  {
    label: 'Stakeholders',
    items: [
      {
        // Its own group rather than under Design, because a concern is the one
        // thing here that is NOT part of the design: it is what somebody
        // outside the program cares about, and it exists so the regulatory and
        // operational segments can be traced without pretending they are
        // components. Filing it under Design would undo the distinction the
        // model was reshaped to make.
        label: 'Concerns',
        path: '/concerns',
        icon: <GavelIcon />,
        tooltip: 'What a stakeholder cares about, and the requirements answering it',
        kind: 'concern',
      },
    ],
  },
  {
    label: 'Verification',
    items: [
      {
        label: 'Verification',
        path: '/verification',
        icon: <ScienceOutlinedIcon />,
        tooltip: 'Test cases, plans and campaigns',
        kind: 'verification',
      },
      {
        label: 'Flowdown',
        path: '/verification/flowdown',
        icon: <AccountTreeIcon />,
        tooltip: 'Campaign down to case, through the usages that route it',
      },
      {
        label: 'Traceability',
        path: '/verification/rtm',
        icon: <GridOnIcon />,
        tooltip: 'The RVTM: every requirement against the four methods',
      },
    ],
  },
  {
    label: 'Project',
    items: [
      {
        label: 'Version control',
        path: '/version-control',
        icon: <MergeTypeIcon />,
        tooltip: 'Propose this branch for review, and clear up after it merges',
      },
      {
        label: 'Glossary',
        path: '/glossary',
        icon: <MenuBookIcon />,
        tooltip: 'The terms and acronyms used here, and what each means',
      },
    ],
  },
]

/**
 * The sidebar, with any uncovered entity kind appended.
 *
 * @param schema what the back end says can be edited
 */
export function buildNavigation(schema: Schema): NavGroup[] {
  const covered = new Set(
    CURATED.flatMap((group) => group.items.map((item) => item.kind)).filter(Boolean),
  )

  const extra: NavItem[] = schema.kinds
    .filter((kind) => !covered.has(kind.name))
    .map((kind) => ({
      label: readable(kind.plural),
      path: `/${kind.plural}`,
      icon: <CategoryIcon />,
      tooltip: kind.help || `Browse and edit ${kind.label.toLowerCase()}`,
      kind: kind.name,
    }))

  if (!extra.length) return CURATED
  return [...CURATED, { label: 'More', items: extra }]
}

/**
 * A heading from the registry's plural.
 *
 * Taken from the back end rather than pluralised here. Any rule this side
 * would guess wrong eventually - 'analysis' does not become 'analysises' - and
 * the registry already states the plural because the URL needs it.
 */
function readable(plural: string): string {
  const words = plural.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** Every path the nav can reach, for deciding which item is current. */
export function isCurrent(itemPath: string, pathname: string): boolean {
  if (itemPath === '/') return pathname === '/'
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`)
}

/**
 * Which single nav item the current URL belongs to.
 *
 * The LONGEST match wins, which `isCurrent` alone cannot decide because it sees
 * one item at a time. Verification is where that bites: `/verification` is a
 * prefix of `/verification/flowdown`, so a per-item prefix test highlights both
 * the list and the flowdown at once, and a sidebar claiming you are in two
 * places is worse than one claiming the wrong single place.
 *
 * Prefix matching still matters for the item that does win - `/verification/TC_1`
 * has no nav item of its own and belongs to the list it came from.
 *
 * @param groups the built navigation
 * @param pathname the current location
 * @returns the winning item's path, or '' when nothing matches
 */
export function currentPath(groups: NavGroup[], pathname: string): string {
  let best = ''
  for (const group of groups) {
    for (const item of group.items) {
      if (isCurrent(item.path, pathname) && item.path.length > best.length) {
        best = item.path
      }
    }
  }
  return best
}
